import { ReactNode, useLayoutEffect } from 'react';

import { useServiceOffers } from 'features/offer/state/service-offers-context';
import { usePaymentContext } from 'features/payment';

import { useFavoritesContext } from 'state/favorites';
import { useLoyaltyUserStateCleanup } from 'state/loyalty/hooks/use-loyalty-user-state-cleanup';
import { useInRestaurantRedemptionContext } from 'state/loyalty/in-restaurant-redemption';
import { useOrderContext } from 'state/order';

import { useThirdPartyAuthentication } from './hooks/use-third-party-authentication';
import { configureAuthUserStateCleanup } from './session-transition';
import { clearRetiredUserStorage } from './user-state-storage';

/**
 * Central composition seam for authenticated-user state cleanup. Feature owners expose one
 * callback; register it here following docs/architecture/authentication-user-state-cleanup.md.
 */
export const AuthUserStateCleanupRegistration = ({ children }: { children: ReactNode }) => {
  const { clearUserOwnedOrderState } = useOrderContext();
  const { clearUserOwnedFavoritesState } = useFavoritesContext();
  const { resetInRestaurantRedemption } = useInRestaurantRedemptionContext();
  const clearLoyaltyUserState = useLoyaltyUserStateCleanup();
  const { clearUserOwnedPaymentState } = usePaymentContext();
  const { appliedOffersBatch } = useServiceOffers();
  const { logUserOutOfThirdPartyServices } = useThirdPartyAuthentication();

  useLayoutEffect(() => {
    configureAuthUserStateCleanup([
      {
        name: 'order-state',
        cleanup: clearUserOwnedOrderState,
      },
      {
        name: 'favorites-provider',
        cleanup: clearUserOwnedFavoritesState,
      },
      {
        name: 'in-restaurant-redemption',
        cleanup: resetInRestaurantRedemption,
      },
      {
        name: 'loyalty-redux',
        cleanup: clearLoyaltyUserState,
      },
      {
        name: 'retained-applied-offer-metadata',
        cleanup: appliedOffersBatch.clearRetainedMetadata,
      },
      {
        name: 'payment-provider',
        cleanup: clearUserOwnedPaymentState,
      },
      {
        name: 'user-state-storage',
        cleanup: clearRetiredUserStorage,
      },
      {
        name: 'third-party-authentication',
        cleanup: logUserOutOfThirdPartyServices,
      },
    ]);

    return () => configureAuthUserStateCleanup([]);
  }, [
    appliedOffersBatch.clearRetainedMetadata,
    clearUserOwnedPaymentState,
    clearLoyaltyUserState,
    clearUserOwnedFavoritesState,
    clearUserOwnedOrderState,
    logUserOutOfThirdPartyServices,
    resetInRestaurantRedemption,
  ]);

  return children;
};
